import { useCallback, useMemo, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableRow,
  FilterBar,
  FormField,
  FormSection,
  PageHeader,
  RowNumberCell,
  toast,
} from "../../../components/ui";
import {
  Box,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  MatterPreviewResponse,
  TimeEntryReportModel,
  TimeEntryViewModel,
  TimeKeepingReport,
  UsersService,
} from "../../../api";
import { ClioMatterAutocomplete } from "../../../components/shared/MatterAutocomplete";
import { HelpersService } from "../../../services/Helpers";
import { AllSelection, Presets } from "../ReportsOptions";
import { startSpinner, stopSpinner } from "../../../redux/actions/spinnerActions";

type FlatEntry = TimeEntryReportModel & {
  dayLabel?: string;
  userName?: string | null;
};

function formatDateToYYYYMMDD(date: Dayjs | null | undefined) {
  if (!date) return undefined;
  return `${date.year()}-${date.month() + 1}-${date.date()}`;
}

function formatDayLabel(day?: string) {
  if (!day) return "—";
  return new Date(day).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
}

function ExpandToggle({
  open,
  onClick,
}: {
  open: boolean;
  onClick: () => void;
}) {
  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-label={open ? "Collapse" : "Expand"}
      sx={{
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s",
      }}
    >
      <ExpandMoreIcon fontSize="small" />
    </IconButton>
  );
}

export default function Timekeeping() {
  const [description, setDescription] = useState("");
  const [hours, setHours] = useState<number | "">("");
  const [minutes, setMinutes] = useState<number | "">("");
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().startOf("month")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [userTimes, setUserTimes] = useState<TimeKeepingReport[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedMatter, setSelectedMatter] =
    useState<MatterPreviewResponse | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>("Month");
  const [updateId, setUpdateId] = useState<number | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>(
    {}
  );
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});
  const [hasLoaded, setHasLoaded] = useState(false);

  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user?.user);

  const refreshTimeReport = useCallback(() => {
    dispatch(startSpinner());
    UsersService.getApiUsersGetTimeEntries(
      formatDateToYYYYMMDD(startDate),
      formatDateToYYYYMMDD(endDate)
    )
      .then((times) => {
        setUserTimes(times ?? []);
        setHasLoaded(true);
        const nextExpanded: Record<string, boolean> = {};
        (times ?? []).forEach((row, index) => {
          const key = row.userName ?? `user-${index}`;
          nextExpanded[key] = true;
        });
        setExpandedUsers(nextExpanded);
      })
      .catch(() => {
        setUserTimes([]);
        toast.error("Could not load time entries.");
      })
      .finally(() => dispatch(stopSpinner()));
  }, [startDate, endDate, dispatch]);

  const clearData = () => {
    setSelectedDate(null);
    setDescription("");
    setHours("");
    setMinutes("");
    setUpdateId(null);
    setSelectedMatter(null);
  };

  const handleAddTimeEntry = () => {
    const hoursNum = typeof hours === "number" ? hours : 0;
    const minutesNum = typeof minutes === "number" ? minutes : 0;
    const seconds = hoursNum * 3600 + minutesNum * 60;

    if (!seconds || seconds < 0 || !description.trim()) {
      toast.error("Time and description are required.");
      return;
    }

    let desc = description.trim();
    if (selectedMatter?.description) {
      desc = `${selectedMatter.description} --- ${desc}`;
    }

    const payload = {
      id: updateId ?? undefined,
      durationSeconds: seconds,
      description: desc,
      entryDate: formatDateToYYYYMMDD(
        selectedDate ? dayjs(selectedDate) : dayjs()
      ),
    } as TimeEntryViewModel;

    dispatch(startSpinner());
    const request = updateId
      ? UsersService.postApiUsersUpdateTimeEntry(payload)
      : UsersService.postApiUsersAddTimeEntry(payload);

    request
      .then(() => {
        clearData();
        refreshTimeReport();
        toast.success(updateId ? "Time entry updated." : "Time entry added.");
      })
      .catch(() => {
        toast.error(
          updateId ? "Time entry update failed." : "Time entry failed."
        );
      })
      .finally(() => dispatch(stopSpinner()));
  };

  const handlePresetChange = (value: string) => {
    setSelectedPreset(value);
    const currentDate = dayjs();
    let nextStart: Dayjs | null = null;
    let nextEnd = dayjs();

    switch (value) {
      case "LastMonth":
        nextStart = currentDate.startOf("month").add(-1, "month");
        nextEnd = currentDate.startOf("month").add(-1, "day");
        break;
      case "Month":
        nextStart = currentDate.startOf("month");
        break;
      case "Week":
        nextStart = currentDate.startOf("week");
        break;
      case "Day":
        nextStart = currentDate.startOf("day");
        break;
      default:
        nextStart = null;
        break;
    }

    setStartDate(nextStart);
    setEndDate(nextEnd);
  };

  const handleEntryClick = (entry: FlatEntry) => {
    if (!entry.id || entry.userId !== user?.id) return;

    setDescription(entry.description ?? "");
    const total = entry.total ?? 0;
    const nextHours = Math.floor(total / 3600);
    const nextMinutes = Math.floor((total - nextHours * 3600) / 60);
    setHours(nextHours);
    setMinutes(nextMinutes);
    setSelectedDate(entry.date ? dayjs(entry.date) : null);
    setUpdateId(entry.id);
    setSelectedMatter(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleUser = (key: string) => {
    setExpandedUsers((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleDay = (key: string) => {
    setExpandedDays((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const reportRows = useMemo(() => userTimes ?? [], [userTimes]);

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      <PageHeader
        title="Time tracking"
        subtitle="Log time against matters and review entries by date range."
      />

      <Stack spacing={2.5}>
        <FormSection
          title={updateId ? "Edit time entry" : "Log time entry"}
          subtitle={
            updateId
              ? "Update the selected entry, then save."
              : "Add hours and a description for the selected date."
          }
          icon={<AccessTimeIcon />}
          headerEnd={
            updateId ? (
              <Typography variant="caption" color="primary.main" sx={{ pt: 0.5 }}>
                Editing entry #{updateId}
              </Typography>
            ) : undefined
          }
        >
          <Box className="row g-3">
            <Box className="col-6 col-md-2">
              <FormField
                label="Hours"
                type="number"
                size="small"
                fullWidth
                value={hours === "" ? "" : String(hours)}
                onChange={(e) =>
                  setHours(e.target.value === "" ? "" : Number(e.target.value))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">h</InputAdornment>
                  ),
                  inputProps: { min: 0 },
                }}
              />
            </Box>
            <Box className="col-6 col-md-2">
              <FormField
                label="Minutes"
                type="number"
                size="small"
                fullWidth
                value={minutes === "" ? "" : String(minutes)}
                onChange={(e) =>
                  setMinutes(
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">m</InputAdornment>
                  ),
                  inputProps: { min: 0, max: 59 },
                }}
              />
            </Box>
            <Box className="col-12 col-md-4">
              <DatePicker
                disableFuture
                label="Entry date"
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
            </Box>
            <Box className="col-12 col-md-4">
              <ClioMatterAutocomplete
                value={selectedMatter}
                onChange={setSelectedMatter}
                label="Matter"
                size="small"
                fullWidth
              />
            </Box>
            <Box className="col-12">
              <FormField
                required
                multiline
                minRows={2}
                label="Description"
                size="small"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Box>
            <Box className="col-12">
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <AppButton
                  variant="contained"
                  color="primary"
                  onClick={handleAddTimeEntry}
                >
                  {updateId ? "Update" : "Add"} time entry
                </AppButton>
                {updateId != null && (
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    onClick={clearData}
                  >
                    Cancel
                  </AppButton>
                )}
              </Stack>
            </Box>
          </Box>
        </FormSection>

        <FormSection
          title="Report"
          subtitle="Browse logged time by person and day. Click your own entry to edit."
          icon={<AssessmentOutlinedIcon />}
        >
          <Box sx={{ mb: 2 }}>
            <FilterBar
              hideFilterButton
              hideClearButton
              leading={
                <>
                  <DatePicker
                    disableFuture
                    label="Start date"
                    value={startDate}
                    onChange={(date) => {
                      setSelectedPreset(AllSelection);
                      setStartDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: { xs: "100%", sm: 160 },
                          bgcolor: "background.paper",
                          borderRadius: 1,
                        },
                      },
                    }}
                  />
                  <DatePicker
                    disableFuture
                    label="End date"
                    value={endDate}
                    onChange={(date) => {
                      setSelectedPreset(AllSelection);
                      setEndDate(date);
                    }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: { xs: "100%", sm: 160 },
                          bgcolor: "background.paper",
                          borderRadius: 1,
                        },
                      },
                    }}
                  />
                  <FormField
                    select
                    size="small"
                    label="Preset"
                    fullWidth={false}
                    value={selectedPreset}
                    onChange={(e) => handlePresetChange(String(e.target.value))}
                    sx={{
                      width: { xs: "100%", sm: 160 },
                      bgcolor: "background.paper",
                      borderRadius: 1,
                    }}
                  >
                    <MenuItem value={AllSelection}>Select all</MenuItem>
                    {Object.keys(Presets)
                      .filter((key) => isNaN(Number(key)))
                      .map((key) => (
                        <MenuItem key={key} value={key}>
                          {HelpersService.getEnumKeyByEnumValue(
                            Presets,
                            Presets[key as keyof typeof Presets]
                          )}
                        </MenuItem>
                      ))}
                  </FormField>
                </>
              }
              trailing={
                <AppButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={refreshTimeReport}
                >
                  Load Report
                </AppButton>
              }
            />
          </Box>

          <Stack spacing={1.5}>
            {reportRows.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {hasLoaded
                  ? "No time entries found for this date range."
                  : "Click Load Report to view time entries."}
              </Typography>
            )}

            {reportRows.map((userRow, userIndex) => {
              const userKey = userRow.userName ?? `user-${userIndex}`;
              const userOpen = expandedUsers[userKey] ?? false;
              const days = userRow.timesByDays ?? [];

              return (
                <Box
                  key={userKey}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: "background.paper",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      bgcolor: "#fafbfd",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleUser(userKey)}
                  >
                    <ExpandToggle
                      open={userOpen}
                      onClick={() => toggleUser(userKey)}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600 }}
                        noWrap
                      >
                        {userRow.userName || "Unknown user"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
                    >
                      {userRow.totalHours || "0h"}
                    </Typography>
                  </Stack>

                  <Collapse in={userOpen} timeout="auto" unmountOnExit>
                    <Stack spacing={1} sx={{ p: 1.5, pt: 1 }}>
                      {days.map((dayRow, dayIndex) => {
                        const dayKey = `${userKey}-${dayRow.day ?? dayIndex}`;
                        const dayOpen = expandedDays[dayKey] ?? true;
                        const entries = dayRow.times ?? [];

                        return (
                          <Box
                            key={dayKey}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1.5,
                              overflow: "hidden",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{
                                px: 1.25,
                                py: 1,
                                bgcolor: "grey.50",
                                cursor: "pointer",
                              }}
                              onClick={() => toggleDay(dayKey)}
                            >
                              <ExpandToggle
                                open={dayOpen}
                                onClick={() => toggleDay(dayKey)}
                              />
                              <Typography
                                variant="body2"
                                sx={{ flex: 1, fontWeight: 500 }}
                              >
                                {formatDayLabel(dayRow.day)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: "nowrap" }}
                              >
                                {dayRow.totalHours || "0h"}
                              </Typography>
                            </Stack>

                            <Collapse
                              in={dayOpen}
                              timeout="auto"
                              unmountOnExit
                            >
                              <AppTable
                                columns={[
                                  {
                                    id: "totalHours",
                                    label: "Time",
                                    sortable: false,
                                  },
                                  {
                                    id: "description",
                                    label: "Description",
                                    sortable: false,
                                  },
                                ]}
                                showRowNumber
                                size="small"
                                maxHeight={360}
                                isEmpty={entries.length === 0}
                                emptyTitle="No entries"
                                emptyDescription="No time logged for this day."
                              >
                                {entries.map((entry, entryIndex) => {
                                  const canEdit =
                                    !!entry.id && entry.userId === user?.id;
                                  return (
                                    <AppTableRow
                                      key={entry.id ?? `${dayKey}-${entryIndex}`}
                                      hover={canEdit}
                                      onClick={() =>
                                        canEdit &&
                                        handleEntryClick({
                                          ...entry,
                                          dayLabel: formatDayLabel(dayRow.day),
                                          userName: userRow.userName,
                                        })
                                      }
                                      sx={{
                                        cursor: canEdit ? "pointer" : "default",
                                      }}
                                    >
                                      <RowNumberCell value={entryIndex + 1} />
                                      <AppTableCell>
                                        {entry.totalHours || "—"}
                                      </AppTableCell>
                                      <AppTableCell>
                                        {entry.description || "—"}
                                      </AppTableCell>
                                    </AppTableRow>
                                  );
                                })}
                              </AppTable>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </FormSection>
      </Stack>
    </Box>
  );
}
